import * as React from 'react';
import { Dialog as SheetPrimitive } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

import { useStrings } from '@/i18n/use-strings';
import { Icon } from './icon';

/**
 * The shadcn `sheet`, with these deliberate changes from the registry copy:
 *
 * - `lucide-react` is not a dependency here, so the close glyph is a sprite
 *   `Icon` (see the README on icons);
 * - the `right`/`left` sides are replaced by the logical `end`/`start`, which
 *   is what an RTL language needs — a menu that opens from the inline end lands
 *   on the left in Arabic without a second component;
 * - the scrim and the elevation are `DialogContent`'s, not the registry's, so
 *   a sheet and a dialog sit on the same ground and cast the same shadow. They
 *   are the same kind of surface; they were arriving on different terms and at
 *   different speeds.
 */
type SheetSide = 'top' | 'bottom' | 'start' | 'end';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]',
        // The ticket-11 tokens, as `DialogOverlay` uses them. Static blur: set
        // once when it mounts, never animated, so it costs one composite and
        // nothing per frame.
        'duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)] data-[state=closed]:duration-[var(--bp-duration-fast)]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The panel classes per side. Both inline sides slide from their physical edge
 * and flip under `rtl:`, so the panel always enters from the edge it sits on.
 */
const sideClasses: Record<SheetSide, string> = {
  end: 'inset-y-0 end-0 h-full w-full border-s sm:max-w-sm data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right rtl:data-[state=open]:slide-in-from-left rtl:data-[state=closed]:slide-out-to-left',
  start:
    'inset-y-0 start-0 h-full w-full border-e sm:max-w-sm data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left rtl:data-[state=open]:slide-in-from-right rtl:data-[state=closed]:slide-out-to-right',
  top: 'inset-x-0 top-0 h-auto border-b data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
  bottom:
    'inset-x-0 bottom-0 h-auto border-t data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
};

function SheetContent({
  className,
  children,
  side = 'end',
  showCloseButton = true,
  closeLabel,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: SheetSide;
  showCloseButton?: boolean;
  /** Accessible name for the close button. Defaults to the catalogue's `header_close`. */
  closeLabel?: string;
}) {
  const { header } = useStrings();

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 overscroll-contain',
          // The app's elevation and the app's easing, both shared with
          // `DialogContent` — the registry's `shadow-lg` and its own duration
          // pair made the panel a different kind of object to the dialog.
          'shadow-[0_32px_80px_-34px_hsl(var(--surf-dark-blue)/0.85)]',
          'duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)] data-[state=closed]:duration-[var(--bp-duration-fast)] data-[state=closed]:ease-[var(--bp-ease-in)]',
          // Paints under the home indicator and the cutout, then puts both
          // back — a sheet is a fixed edge like the header is.
          'pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]',
          // Under reduced motion the panel crossfades in place. `bp-overlay`
          // rather than four `motion-reduce:[--tw-*]` classes: those sit a
          // specificity below the `data-[state]` slide utilities they have to
          // beat, so they never fired. See the rule in `app.css`.
          'bp-overlay',
          sideClasses[side],
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton ? (
          /*
            The cutout's inset is added to the offset, not just to the panel:
            an absolutely positioned button is placed against the padding box,
            so `top-3` alone put the only way out of a full-height sheet under
            the notch on a phone that has one. `active:scale-96` is the press
            `DialogContent`'s close answers with.
          */
          <SheetPrimitive.Close
            className="text-surf-blue focus-visible:ring-surf-blue/40 absolute end-3 top-[calc(env(safe-area-inset-top,0px)+0.75rem)] z-10 grid size-11 place-items-center rounded-full transition-[background-color,scale] duration-150 hover:bg-black/5 focus-visible:ring-4 focus-visible:outline-none active:scale-96 disabled:pointer-events-none motion-reduce:transition-none"
            aria-label={closeLabel ?? header.header_close}
          >
            <Icon name="passport/close" className="size-5" />
          </SheetPrimitive.Close>
        ) : null}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-header" className={cn('flex flex-col gap-1.5 px-5 pe-16 pt-5', className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-footer" className={cn('mt-auto flex flex-col gap-2 p-5', className)} {...props} />;
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-bebas text-surf-dark-blue text-3xl leading-none tracking-wide uppercase', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
export type { SheetSide };
